import { useState } from 'react';

type Preset = 'AUTO' | 'KARAOKE_CLEAN' | 'BOLD_POP' | 'KEYWORD_HIGHLIGHT' | 'MINIMAL_CLEAN' | 'KINETIC_GAMING';
type Placement = 'AUTO' | 'LOWER_CENTER' | 'CENTER' | 'UPPER_CENTER' | 'LEFT' | 'RIGHT';

interface Props {
  aspectRatio: '9:16' | '16:9';
  preset: Preset;
  placement: Placement;
  maxWordsPerGroup: number;
  fontSize: number;
}

interface PreviewJob { jobId: string; status: string; progress: number; error: string | null; url: string | null }

const sampleText = (language: 'en' | 'pl', aspectRatio: Props['aspectRatio']) => language === 'pl'
  ? aspectRatio === '9:16' ? 'Zobacz, jak animowane napisy podświetlają najważniejsze słowa.' : 'Najpierw sprawdźmy wynik, a potem wyjaśnię, co oznaczają te liczby.'
  : aspectRatio === '9:16' ? 'Watch the captions highlight each spoken word in this quick example.' : 'Let’s check the result first, then I’ll explain what these numbers mean.';

export function PinsCaptionPreview(props: Props) {
  const [language, setLanguage] = useState<'en' | 'pl'>('en');
  const [text, setText] = useState(() => sampleText('en', props.aspectRatio));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewJob | null>(null);

  const render = async () => {
    if (busy) return;
    setBusy(true); setMessage(null); setPreview(null);
    try {
      const startedResponse = await fetch('/api/pins/captions/test', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...props, language, text, aspectRatio: props.aspectRatio }),
      });
      const started = await startedResponse.json().catch(() => null) as { jobId?: string; status?: string; error?: { message?: string } } | null;
      if (!startedResponse.ok || !started?.jobId) throw new Error(started?.error?.message ?? `Nie udało się uruchomić podglądu (${startedResponse.status})`);
      setPreview({ jobId: started.jobId, status: started.status ?? 'QUEUED', progress: 0, error: null, url: null });
      for (let attempt = 0; attempt < 90; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const response = await fetch(`/api/pins/captions/test/${encodeURIComponent(started.jobId)}`);
        const result = await response.json().catch(() => null) as PreviewJob | { error?: { message?: string } } | null;
        if (!response.ok || !result || !('jobId' in result)) throw new Error((result as { error?: { message?: string } } | null)?.error?.message ?? `Nie udało się sprawdzić podglądu (${response.status})`);
        setPreview(result);
        if (result.status === 'COMPLETED' && result.url) return;
        if (['FAILED', 'CANCELLED'].includes(result.status)) throw new Error(result.error ?? 'Render podglądu nie powiódł się');
      }
      setMessage('Podgląd nadal renderuje się w kolejce. Możesz wrócić później; zadanie nie publikuje materiału.');
    } catch (error) { setMessage((error as Error).message); }
    finally { setBusy(false); }
  };

  return <div style={{ marginTop: 10, padding: 10, border: '1px solid var(--border)', borderRadius: 8 }}>
    <strong>Podgląd napisów · próbka TTS</strong>
    <p className="muted" style={{ fontSize: 12, margin: '4px 0 8px' }}>Render 5–10 s używa tego samego Remotion Caption Engine i lokalnego Piper. To osobny podgląd bez publikacji; jeśli nie ma word alignment, wynik oznacza synchronizację frazową.</p>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <label>Język <select value={language} disabled={busy} onChange={(event) => { const next = event.target.value as 'en' | 'pl'; setLanguage(next); setText(sampleText(next, props.aspectRatio)); }}><option value="en">English</option><option value="pl">Polski</option></select></label>
      <button className="btn btn--sm btn--primary" disabled={busy || !text.trim() || text.trim().split(/\s+/u).length > 18} onClick={() => void render()}>{busy ? 'Renderuję…' : 'Renderuj podgląd napisów'}</button>
      <span className="muted">Limit: 18 słów · preset {props.preset}</span>
    </div>
    <textarea aria-label="Tekst podglądu napisów" value={text} maxLength={220} disabled={busy} onChange={(event) => setText(event.target.value)} style={{ display: 'block', width: '100%', minHeight: 64, marginTop: 8 }} />
    {preview && <div style={{ marginTop: 8 }}><span>{preview.status}{!['COMPLETED', 'FAILED', 'CANCELLED'].includes(preview.status) ? ` · ${preview.progress}%` : ''}</span>{preview.url && <video controls playsInline preload="metadata" src={preview.url} style={{ display: 'block', width: 'min(100%, 360px)', maxHeight: 560, marginTop: 8, background: '#111827' }} />}{preview.error && <div role="alert" className="muted">{preview.error}</div>}</div>}
    {message && <div className="muted" role="status" style={{ marginTop: 6 }}>{message}</div>}
  </div>;
}
